import React, { useRef, useState, useEffect } from 'react';
import { motion, useReducedMotion, AnimatePresence, useSpring } from 'framer-motion';
import { Mail, Filter, Edit, Send } from 'lucide-react';
import { cn } from '@/lib/utils';

// TypeScript interfaces
interface Step {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: 'envelope' | 'filter' | 'pen' | 'check';
}
interface StepTextProps {
  step: Step;
  isActive: boolean;
  isVisible: boolean;
  className?: string;
}
interface MockUIProps {
  isVisible: boolean;
  isActive?: boolean;
}
interface Email {
  id: number;
  sender: string;
  avatar?: string;
  subject: string;
  preview: string;
  time: string;
  isNew: boolean;
}
interface PriorityEmail {
  id: number;
  sender: string;
  subject: string;
  preview: string;
  priority: 'needs_response' | 'auto_handled' | 'scheduled';
  aiDecision?: string;
  confidence?: number;
}

// Animated counter component with number ticker and spring animations
const AnimatedCounter = ({
  value
}: {
  value: number;
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const springValue = useSpring(0, {
    stiffness: 300,
    damping: 30
  });
  useEffect(() => {
    springValue.set(value);
    const unsubscribe = springValue.onChange(v => {
      setDisplayValue(Math.round(v));
    });
    return unsubscribe;
  }, [value, springValue]);
  return <motion.div key={value} initial={{
    scale: 0,
    rotate: -180,
    filter: 'blur(4px)'
  }} animate={{
    scale: 1,
    rotate: 0,
    filter: 'blur(0px)',
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 20
    }
  }} exit={{
    scale: 0.8,
    rotate: 180,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }} className="ml-auto relative" data-id="element-160">
      {/* Glow effect */}
      <div className="absolute inset-0 bg-red-500 blur-md opacity-40 scale-150" data-id="element-161" />
      
      {/* Counter badge */}
      <div className="relative bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium" data-id="element-162">
        {displayValue} unread
      </div>
    </motion.div>;
};

// Data structure for steps
const steps: Step[] = [{
  id: 'detect',
  number: '01',
  title: 'Detect',
  description: 'RealtyZen monitors your Gmail inbox 24/7, instantly detecting new emails from clients, prospects, and partners.',
  detail: 'Response in < 2 minutes',
  icon: 'envelope'
}, {
  id: 'filter',
  number: '02',
  title: 'Prioritize',
  description: 'AI analyzes each message to identify urgent inquiries, tour requests, and offers that need your immediate attention.',
  detail: '95% accuracy rate',
  icon: 'filter'
}, {
  id: 'draft',
  number: '03',
  title: 'Draft',
  description: 'Creates personalized responses matching your writing style, saved directly to your Gmail drafts folder for review.',
  detail: 'Your voice, automated',
  icon: 'pen'
}, {
  id: 'approve',
  number: '04',
  title: 'Approve',
  description: 'Review and send with one click from Gmail, or use voice commands while driving between showings.',
  detail: 'Hands-free ready',
  icon: 'check'
}];

// Clean step text component with vertical accent line
const StepText: React.FC<StepTextProps> = ({
  step,
  isActive,
  isVisible,
  className
}) => {
  const shouldReduceMotion = useReducedMotion();
  const isRightAligned = className?.includes('text-right');
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={isVisible ? {
    opacity: 1,
    y: 0
  } : {}} transition={shouldReduceMotion ? {} : {
    duration: 0.6
  }} className={cn("relative h-full flex items-center", className)} data-id="element-163">
      {/* Vertical accent line - hidden on mobile */}
      <motion.div className={cn("absolute left-0 top-0 bottom-0 w-1 rounded-full transition-all duration-300 hidden md:block", isActive ? "bg-sage-600 opacity-100" : "bg-slate-300 opacity-40")} animate={shouldReduceMotion ? {} : {
      scaleY: isActive ? 1 : 0.7
    }} style={{
      transformOrigin: 'center'
    }} data-id="element-164" />
      
      {/* Content with responsive left padding */}
      <div className="pl-0 md:pl-8 w-full" data-id="element-165">
        {/* Number badge */}
        <div className={cn(isRightAligned ? 'flex justify-end' : '', 'mb-3 md:mb-6')} data-id="element-166">
          <motion.div className={cn("inline-flex items-center justify-center", "w-12 h-12 md:w-16 md:h-16 rounded-full", "text-lg md:text-xl font-bold transition-all duration-300", isActive ? "bg-sage-600 text-white shadow-lg shadow-sage-600/30" : "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400")} animate={shouldReduceMotion ? {} : {
          scale: isActive ? 1.05 : 1
        }} data-id="element-167">
            {step.number}
          </motion.div>
        </div>
        
        {/* Step content */}
        <div className="space-y-4" data-id="element-168">
          <h3 className={cn("font-display text-2xl md:text-3xl font-semibold transition-colors duration-300", isActive ? "text-slate-900 dark:text-white" : "text-slate-600 dark:text-slate-400")} data-id="element-169">
            {step.title}
          </h3>
          <p className={cn("text-base md:text-lg leading-relaxed transition-colors duration-300", isActive ? "text-gray-700 dark:text-slate-300" : "text-gray-500 dark:text-slate-500")} data-id="element-170">
            {step.description}
          </p>
          {step.detail && isActive && <motion.span initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="inline-block px-3 md:px-4 py-1.5 md:py-2 bg-sage-50 text-sage-700 rounded-full text-sm md:text-base font-medium" data-id="element-171">
              {step.detail}
            </motion.span>}
        </div>
      </div>
    </motion.div>;
};

// Email arrival pulse effect component
const EmailArrivalPulse = ({
  trigger
}: {
  trigger: number;
}) => {
  return <AnimatePresence data-id="element-172">
      {trigger > 0 && <motion.div key={trigger} className="absolute inset-0 pointer-events-none" initial={{
      opacity: 0
    }} animate={{
      opacity: [0, 0.3, 0]
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.6
    }} data-id="element-173">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-400/20 to-transparent" data-id="element-174" />
        </motion.div>}
    </AnimatePresence>;
};

// Enhanced animated Gmail inbox
const MockInboxUI: React.FC<MockUIProps> = ({
  isVisible,
  isActive = false
}) => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [animationCycle, setAnimationCycle] = useState(0);
  const [pulseCounter, setPulseCounter] = useState(0);
  const shouldReduceMotion = useReducedMotion();

  // Email data - all marked as unread
  const emailData: Email[] = [{
    id: 1,
    sender: "Sarah Johnson",
    avatar: "https://i.pravatar.cc/150?u=sarah",
    subject: "Interested in 4BR house on Maple",
    preview: "Hi, I saw your listing for the property on Maple Street and would love to...",
    time: "2:34 PM",
    isNew: true // All unread
  }, {
    id: 2,
    sender: "Mike Chen",
    avatar: "https://i.pravatar.cc/150?u=mike",
    subject: "Tour request for downtown condo",
    preview: "Would love to schedule a viewing this weekend if possible...",
    time: "2:35 PM",
    isNew: true // All unread
  }, {
    id: 3,
    sender: "Emily Davis",
    avatar: "https://i.pravatar.cc/150?u=emily",
    subject: "Question about property features",
    preview: "Could you provide more details about the kitchen renovation...",
    time: "2:36 PM",
    isNew: true // All unread
  }];
  useEffect(() => {
    if (isVisible && isActive && !shouldReduceMotion) {
      // Clear and restart animation
      const runAnimation = () => {
        setEmails([]); // Clear inbox

        // Stagger emails appearing with pulse effects - much faster timing
        const timer1 = setTimeout(() => {
          setEmails([emailData[0]]);
          setPulseCounter(prev => prev + 1);
        }, 100);
        const timer2 = setTimeout(() => {
          setEmails([emailData[0], emailData[1]]);
          setPulseCounter(prev => prev + 1);
        }, 380);
        const timer3 = setTimeout(() => {
          setEmails([emailData[0], emailData[1], emailData[2]]);
          setPulseCounter(prev => prev + 1);
        }, 460);
        return [timer1, timer2, timer3];
      };

      // Initial run with reduced delay
      let timers: NodeJS.Timeout[] = [];
      const initialTimer = setTimeout(() => {
        timers = runAnimation();
      }, 100);

      // Loop every 6 seconds for snappier demo
      const loopInterval = setInterval(() => {
        setAnimationCycle(prev => prev + 1);
        timers = runAnimation();
      }, 6000);
      return () => {
        clearTimeout(initialTimer);
        timers.forEach(clearTimeout);
        clearInterval(loopInterval);
      };
    } else if (isVisible && !isActive) {
      // Show all emails without animation when not active
      setEmails(emailData);
    } else if (isVisible && shouldReduceMotion) {
      // For reduced motion, show all emails immediately
      setEmails(emailData);
    }
  }, [isVisible, isActive, shouldReduceMotion]);
  return <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-700 relative" style={{
    height: '400px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  }} data-id="element-175">
      {/* Pulse effect overlay */}
      <EmailArrivalPulse trigger={pulseCounter} data-id="element-176" />
      {/* Gmail header with dynamic count */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-700" data-id="element-177">
        <div className="w-8 h-8 flex items-center justify-center" data-id="element-178">
          <img src="https://ssl.gstatic.com/ui/v1/icons/mail/rfr/gmail.ico" alt="Gmail" className="w-7 h-7" onError={e => {
          // Fallback to inline SVG if image fails
          e.currentTarget.style.display = 'none';
          const fallback = e.currentTarget.nextElementSibling as HTMLElement;
          if (fallback) fallback.style.display = 'block';
        }} data-id="element-179" />
          <svg viewBox="0 0 256 193" className="w-7 h-7 hidden" data-id="element-180">
            <path fill="#4285F4" d="M58.182 192h53.818V93.818L0 0v148c0 24.301 19.699 44 44 44h14.182z" data-id="element-181" />
            <path fill="#34A853" d="M197.818 192h14.182c24.301 0 44-19.699 44-44V0l-112 93.818V192z" data-id="element-182" />
            <path fill="#FBBC04" d="M58.182 48v144h53.818v-98.182L58.182 48z" data-id="element-183" />
            <path fill="#EA4335" d="M197.818 48v144h53.818V48l-53.818 45.818z" data-id="element-184" />
            <path fill="#C5221F" d="M0 0l58.182 48 53.818-48L58.182 48 0 0z" data-id="element-185" />
            <path fill="#34A853" d="M256 0l-58.182 48-53.818-48 53.818 48L256 0z" data-id="element-186" />
          </svg>
        </div>
        <span className="font-medium text-slate-900 dark:text-white" data-id="element-187">Inbox</span>
        
        {/* Dynamic unread count */}
        {emails.length > 0 && <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.15,
        ease: "easeOut"
      }} className="ml-auto bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium" data-id="element-188">
            <AnimatePresence mode="wait" data-id="element-189">
              <motion.span key={emails.length} initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 10
          }} transition={{
            duration: 0.1
          }} style={{
            display: 'inline-block'
          }} data-id="element-190">
                {emails.length}
              </motion.span>
            </AnimatePresence>
            {' '}unread
          </motion.div>}
      </div>
      
      {/* Email list container with fixed height and no scrollbar flash */}
      <div className="flex-1 overflow-hidden relative" data-id="element-191">
        <div className="absolute inset-0 overflow-y-auto overscroll-contain email-container hide-scrollbar" style={{
        WebkitOverflowScrolling: 'touch',
        overflowX: 'hidden',
        minHeight: '100%',
        scrollbarGutter: 'stable'
      }} data-id="element-192">
          <motion.div className="divide-y divide-slate-100 dark:divide-slate-800" layout layoutRoot data-id="element-193">
            {/* Reserve minimum height to prevent layout shift */}
            <div style={{
            minHeight: emails.length === 0 ? '320px' : 'auto'
          }} data-id="element-194">
              <AnimatePresence mode="popLayout" initial={false} data-id="element-195">
                {emails.map((email, index) => <motion.div key={`${animationCycle}-${email.id}`} layout initial={{
                opacity: 0,
                y: 20,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                transition: {
                  duration: 0.3,
                  ease: "easeOut",
                  delay: index * 0.1
                }
              }} exit={{
                opacity: 0,
                transition: {
                  duration: 0.2,
                  ease: "easeOut"
                }
              }} whileHover={{
                scale: 1.005,
                transition: {
                  duration: 0.15
                }
              }} className="flex items-start gap-3 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors email-item animation-optimized" style={{
                willChange: 'transform, opacity'
              }} data-id="element-196">
                    {/* Email content - all marked as unread */}
                    <img src={email.avatar} className="w-10 h-10 rounded-full" alt="" data-id="element-197" />
                    <div className="flex-1 min-w-0" data-id="element-198">
                      <div className="flex items-center justify-between mb-1" data-id="element-199">
                        <span className="text-sm font-semibold text-slate-900 dark:text-white" data-id="element-200">
                          {email.sender}
                        </span>
                        <span className="text-xs text-slate-500" data-id="element-201">{email.time}</span>
                      </div>
                      <div className="text-sm mb-1 font-medium text-slate-900 dark:text-white" data-id="element-202">
                        {email.subject}
                      </div>
                      <div className="text-xs text-slate-500 truncate" data-id="element-203">
                        {email.preview}
                      </div>
                    </div>
                    
                    {/* Unread indicator dot */}
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" data-id="element-204" />
                  </motion.div>)}
          </AnimatePresence>
            </div>
          </motion.div>
        
        {/* Simple empty state */}
        {emails.length === 0 && <div className="flex items-center justify-center h-full text-slate-400 text-sm" data-id="element-205">
            No new emails
          </div>}
        </div>
      </div>
    </div>;
};

// AI Prioritization Flow UI with animated email cards
const MockFilterUI: React.FC<MockUIProps> = ({
  isVisible,
  isActive = false
}) => {
  const [emails, setEmails] = useState<PriorityEmail[]>([]);
  const [animationPhase, setAnimationPhase] = useState<'arrival' | 'analysis' | 'complete'>('arrival');
  const [scanningEmail, setScanningEmail] = useState<number | null>(null);
  const [animationCycle, setAnimationCycle] = useState(0);
  const shouldReduceMotion = useReducedMotion();

  // Animation variants for proper stagger
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.25,
        delayChildren: 0.2
      }
    }
  };
  const cardVariants = {
    hidden: {
      x: 150,
      opacity: 0,
      scale: 0.95
    },
    visible: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 25
      }
    }
  };

  // Email data for the prioritization demo
  const priorityEmailData: PriorityEmail[] = [{
    id: 1,
    sender: "Sarah J.",
    subject: "Tour Request",
    preview: "Hi, I need to schedule a viewing this weekend. My husband and I are very interested...",
    priority: 'needs_response',
    aiDecision: "Urgent client request",
    confidence: 95
  }, {
    id: 2,
    sender: "Property Alert",
    subject: "New Listing",
    preview: "A new 3BR property in downtown just listed for $450k matching your client's criteria...",
    priority: 'auto_handled',
    aiDecision: "Auto-notification",
    confidence: 88
  }, {
    id: 3,
    sender: "Mike Chen",
    subject: "Closing Documents",
    preview: "Thanks for sending those over. Everything looks good to me. Ready to proceed...",
    priority: 'scheduled',
    aiDecision: "Follow-up reminder",
    confidence: 92
  }, {
    id: 4,
    sender: "Emma Wilson",
    subject: "Interested in Maple St",
    preview: "I saw your listing online and would love more details about the recent renovations...",
    priority: 'needs_response',
    aiDecision: "New client inquiry",
    confidence: 97
  }];
  useEffect(() => {
    if (isVisible && isActive && !shouldReduceMotion) {
      const runAnimation = () => {
        // Reset state
        setEmails([]);
        setAnimationPhase('arrival');
        setScanningEmail(null);

        // Phase 1: Email arrival (0-2s)
        const arrivalTimer = setTimeout(() => {
          // Add all emails at once and let animation delays handle the sequencing
          setEmails(priorityEmailData);

          // Start analysis after all emails have animated in
          setTimeout(() => {
            setAnimationPhase('analysis');
          }, priorityEmailData.length * 150 + 500); // 300ms per card delay + buffer
        }, 10);

        // Phase 2: AI scanning - enhanced ripple effect like running hands down blinds
        const analysisTimer = setTimeout(() => {
          // First ripple through all cards - slower and more visible
          priorityEmailData.forEach((email, index) => {
            setTimeout(() => {
              setScanningEmail(email.id);
              setTimeout(() => setScanningEmail(null), 300);
            }, 250 + index * 180);
          });

          // Second ripple through all cards
          priorityEmailData.forEach((email, index) => {
            setTimeout(() => {
              setScanningEmail(email.id);
              setTimeout(() => setScanningEmail(null), 300);
            }, 1200 + index * 180);
          });
        }, priorityEmailData.length * 300 + 1000);

        // Phase 3: Complete state (extended timing for longer analysis)
        const completeTimer = setTimeout(() => {
          setAnimationPhase('complete');
        }, priorityEmailData.length * 300 + 1000 + 2500);
        return [arrivalTimer, analysisTimer, completeTimer];
      };

      // Initial run
      let timers: NodeJS.Timeout[] = runAnimation();

      // Loop every 9 seconds to accommodate longer analysis phase
      const loopInterval = setInterval(() => {
        setAnimationCycle(prev => prev + 1);
        timers = runAnimation();
      }, 9000);
      return () => {
        timers.forEach(clearTimeout);
        clearInterval(loopInterval);
      };
    } else if (isVisible) {
      // For reduced motion or inactive state, show final state
      setEmails(priorityEmailData);
      setAnimationPhase('complete');
    }
  }, [isVisible, isActive, shouldReduceMotion]);
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'needs_response':
        return 'border-green-300/40 bg-green-50/30 dark:bg-green-900/10';
      case 'auto_handled':
        return 'border-gray-200/40 bg-gray-50/30 dark:bg-gray-800/20';
      case 'scheduled':
        return 'border-blue-300/40 bg-blue-50/30 dark:bg-blue-900/10';
      default:
        return 'border-gray-200/40';
    }
  };
  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'needs_response':
        return <div className="w-1.5 h-1.5 rounded-full bg-green-400/60" data-id="element-206" />;
      case 'auto_handled':
        return <div className="w-1.5 h-1.5 rounded-full bg-gray-400/50" data-id="element-207" />;
      case 'scheduled':
        return <div className="w-1.5 h-1.5 rounded-full bg-blue-400/60" data-id="element-208" />;
      default:
        return null;
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: -20,
    scale: 0.95
  }} animate={isVisible ? {
    opacity: 1,
    x: 0,
    scale: 1
  } : {}} transition={{
    duration: 0.5
  }} className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm rounded-xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 overflow-hidden ai-priority-container" style={{
    height: '416px'
  }} data-id="element-209">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-200/50 dark:border-slate-700/50 bg-white/90 dark:bg-slate-900/90" data-id="element-210">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 flex items-center justify-center" data-id="element-211">
          <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-212">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" data-id="element-213" />
          </svg>
        </div>
        <div className="flex-1" data-id="element-214">
          <h4 className="font-semibold text-lg text-slate-900 dark:text-white" data-id="element-215">AI Prioritization</h4>
          <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-216">
            {animationPhase === 'arrival' && 'Emails arriving...'}
            {animationPhase === 'analysis' && 'Analyzing content...'}
            {animationPhase === 'complete' && `${emails.filter(e => e.priority === 'needs_response').length} need your response`}
          </div>
        </div>
        
        {/* Neural network icon during analysis */}
        {animationPhase === 'analysis' && <motion.div animate={{
        rotate: 360
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: "linear"
      }} className="w-6 h-6 text-indigo-500" data-id="element-217">
            <svg fill="currentColor" viewBox="0 0 24 24" data-id="element-218">
              <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" data-id="element-219" />
            </svg>
          </motion.div>}
      </div>
      
      {/* Email cards container */}
      <motion.div className="p-4 space-y-3 overflow-hidden" style={{
      height: 'calc(100% - 80px)'
    }} variants={containerVariants} initial="hidden" animate={emails.length > 0 ? "visible" : "hidden"} data-id="element-220">
        {emails.map((email, index) => <motion.div key={`${animationCycle}-${email.id}`} variants={cardVariants} className={cn("relative p-2 rounded-lg border-2 transition-all duration-300 email-item animation-optimized min-h-[60px]", animationPhase === 'complete' ? getPriorityColor(email.priority) : "border-slate-200 dark:border-slate-700", scanningEmail === email.id && "ring-2 ring-indigo-400 dark:ring-indigo-500 shadow-xl shadow-indigo-300/60 dark:shadow-indigo-800/60 scale-[1.02] bg-indigo-50/20 dark:bg-indigo-900/20")} animate={animationPhase === 'complete' && email.priority === 'needs_response' ? {
        scale: 1.03,
        y: -2
      } : {}} data-id="element-221">
              {/* Scanning overlay - slower for blinds effect */}
              {scanningEmail === email.id && <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-indigo-200/40 to-transparent" initial={{
          x: "-100%"
        }} animate={{
          x: "100%"
        }} transition={{
          duration: 0.7,
          ease: "easeInOut"
        }} data-id="element-222" />}
              
              {/* Email content */}
              <div className="relative flex items-center gap-3 h-full" data-id="element-223">
                <div className="flex-1 min-w-0 py-1" data-id="element-224">
                  {/* Combined sender and subject line */}
                  <div className="flex items-center justify-between mb-1" data-id="element-225">
                    <div className="text-sm font-medium text-slate-900 dark:text-white truncate" data-id="element-226">
                      <span className="font-semibold" data-id="element-227">{email.sender}</span>
                      <span className="text-slate-500 dark:text-slate-400 mx-2" data-id="element-228">•</span>
                      <span className="font-normal" data-id="element-229">{email.subject}</span>
                    </div>
                    {animationPhase === 'complete' && getPriorityIcon(email.priority)}
                  </div>
                  {/* Preview text - single line */}
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate" data-id="element-230">
                    {email.preview}
                  </div>
                </div>
                
              </div>
            </motion.div>)}
        
        {/* Summary stats at bottom */}
        {animationPhase === 'complete' && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex justify-center gap-4 pt-4 border-t border-slate-200/50 dark:border-slate-700/50" data-id="element-231">
            <div className="text-center" data-id="element-232">
              <div className="text-lg font-bold text-green-600 dark:text-green-400" data-id="element-233">
                {emails.filter(e => e.priority === 'needs_response').length}
              </div>
              <div className="text-xs text-slate-500" data-id="element-234">Need response</div>
            </div>
            <div className="text-center" data-id="element-235">
              <div className="text-lg font-bold text-gray-500" data-id="element-236">
                {emails.filter(e => e.priority === 'auto_handled').length}
              </div>
              <div className="text-xs text-slate-500" data-id="element-237">Auto-handled</div>
            </div>
            <div className="text-center" data-id="element-238">
              <div className="text-lg font-bold text-blue-500" data-id="element-239">
                {emails.filter(e => e.priority === 'scheduled').length}
              </div>
              <div className="text-xs text-slate-500" data-id="element-240">Scheduled</div>
            </div>
          </motion.div>}
      </motion.div>
    </motion.div>;
};

// Draft UI with typing animation
const MockDraftUI: React.FC<MockUIProps> = ({
  isVisible,
  isActive = false
}) => {
  const [text, setText] = useState("");
  const [animationCycle, setAnimationCycle] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const fullText = "Hi Sarah,\n\nThanks for your interest! The property at 123 Maple Street is available for viewing this weekend. I have slots open on Saturday at 2 PM or Sunday at 11 AM.\n\nWould either of those times work for you?\n\nBest regards,\nAlex";
  useEffect(() => {
    if (isVisible && isActive) {
      if (shouldReduceMotion) {
        setText(fullText);
      } else {
        const runTypingAnimation = () => {
          setText("");
          let currentIndex = 0;
          const interval = setInterval(() => {
            if (currentIndex < fullText.length) {
              setText(fullText.slice(0, currentIndex + 1));
              currentIndex++;
            } else {
              clearInterval(interval);
            }
          }, 30);
          return interval;
        };

        // Initial run
        let typingInterval = runTypingAnimation();

        // Loop every 7 seconds
        const loopInterval = setInterval(() => {
          setAnimationCycle(prev => prev + 1);
          typingInterval = runTypingAnimation();
        }, 9000);
        return () => {
          clearInterval(typingInterval);
          clearInterval(loopInterval);
        };
      }
    } else if (isVisible && !isActive) {
      // Show full text without animation when not active
      setText(fullText);
    }
  }, [isVisible, isActive, shouldReduceMotion]);
  return <motion.div key={animationCycle} initial={{
    opacity: 0,
    scale: 0.95
  }} animate={isVisible ? {
    opacity: 1,
    scale: 1
  } : {}} transition={{
    duration: 0.5
  }} className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-700" style={{
    height: '400px'
  }} data-id="element-241">
      {/* Compose header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700" data-id="element-242">
        <span className="font-semibold text-slate-900 dark:text-white" data-id="element-243">New Message</span>
        <div className="flex items-center gap-2" data-id="element-244">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="px-3 py-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs rounded-full font-medium" data-id="element-245">
            ✨ AI-Draft
          </motion.div>
        </div>
      </div>
      
      {/* Email fields */}
      <div className="p-4 space-y-3" data-id="element-246">
        <div className="flex items-center gap-2 text-sm" data-id="element-247">
          <span className="text-slate-500 w-12" data-id="element-248">To:</span>
          <span className="font-medium text-slate-900 dark:text-white" data-id="element-249">sarah.johnson@email.com</span>
        </div>
        <div className="flex items-center gap-2 text-sm" data-id="element-250">
          <span className="text-slate-500 w-12" data-id="element-251">Re:</span>
          <span className="font-medium text-slate-900 dark:text-white" data-id="element-252">4BR house on Maple Street</span>
        </div>
        
        {/* Typing animation */}
        <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg min-h-[180px]" data-id="element-253">
          <p className="text-sm whitespace-pre-wrap text-slate-700 dark:text-slate-300" data-id="element-254">
            {text}
            {text.length < fullText.length && !shouldReduceMotion && <span className="inline-block w-2 h-4 bg-indigo-500 animate-pulse ml-0.5" data-id="element-255" />}
          </p>
        </div>
      </div>
    </motion.div>;
};

// Voice Approval Abstraction - Beautiful directional UI
const MockApproveUI: React.FC<MockUIProps> = ({
  isVisible,
  isActive = false
}) => {
  const [phase, setPhase] = useState<'idle' | 'listening' | 'sending'>('idle');
  const [voiceIntensity, setVoiceIntensity] = useState([0.3, 0.5, 0.4, 0.6, 0.3]);
  const [animationCycle, setAnimationCycle] = useState(0);
  const [transcriptionText, setTranscriptionText] = useState('');
  const shouldReduceMotion = useReducedMotion();
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  useEffect(() => {
    if (isVisible && isActive && !shouldReduceMotion) {
      const sequence = async () => {
        // Reset to idle
        setPhase('idle');
        await delay(1000);

        // Start listening
        setPhase('listening');
        setTranscriptionText('');

        // Animate voice intensity
        const intensityInterval = setInterval(() => {
          setVoiceIntensity(prev => prev.map(() => 0.2 + Math.random() * 0.8));
        }, 150);

        // Animated transcription - words appear progressively
        const transcriptionWords = ['Send', 'this', 'email', 'now'];
        for (let i = 0; i < transcriptionWords.length; i++) {
          setTimeout(() => {
            setTranscriptionText(transcriptionWords.slice(0, i + 1).join(' ') + '...');
          }, 400 + i * 350);
        }
        await delay(2500);
        clearInterval(intensityInterval);

        // Skip processing - go straight to sending
        setPhase('sending');
        setTranscriptionText('');
        await delay(1800);
      };

      // Initial run with delay
      const initialTimer = setTimeout(sequence, 500);

      // Loop every 7 seconds since we removed processing phase
      const loopInterval = setInterval(() => {
        setAnimationCycle(prev => prev + 1);
        sequence();
      }, 7000);
      return () => {
        clearTimeout(initialTimer);
        clearInterval(loopInterval);
      };
    } else if (isVisible && !isActive) {
      // Show idle state without animation loop when not active
      setPhase('idle');
    } else if (isVisible && shouldReduceMotion) {
      // For reduced motion, show final state
      setPhase('idle');
    }
  }, [isVisible, isActive, shouldReduceMotion]);
  return <motion.div initial={{
    opacity: 0,
    y: 20,
    scale: 0.95
  }} animate={isVisible ? {
    opacity: 1,
    y: 0,
    scale: 1
  } : {}} transition={{
    duration: 0.5
  }} className="relative" style={{
    height: '410px'
  }} data-id="element-256">
      {/* Background blur layer */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/20 to-purple-100/20 dark:from-indigo-900/10 dark:to-purple-900/10 rounded-2xl blur-xl" data-id="element-257" />
      
      {/* Gradient orb glow effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-full blur-3xl pointer-events-none" data-id="element-258" />
      
      {/* Main glassmorphic container */}
      <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-2xl border border-white/20 dark:border-slate-700/20 shadow-2xl overflow-hidden h-full" data-id="element-259">
        
        {/* Fixed-height draft card section */}
        <div className="h-[120px] px-6 pt-6" data-id="element-260">
          <motion.div key={animationCycle} initial={{
          y: 10,
          opacity: 0
        }} animate={{
          y: phase === 'sending' ? -20 : 0,
          opacity: phase === 'sending' ? 0 : 1
        }} transition={{
          type: "spring",
          stiffness: 300,
          damping: 30
        }} data-id="element-261">
            <div className="bg-gradient-to-br from-white/90 to-slate-50/90 dark:from-slate-800/90 dark:to-slate-900/90 rounded-xl p-3 border border-slate-200/30 dark:border-slate-700/30 shadow-lg shadow-indigo-500/10" data-id="element-262">
              {/* Mini email preview */}
              <div className="text-xs text-slate-500 mb-1" data-id="element-263">Ready to send:</div>
              <div className="text-sm text-slate-700 dark:text-slate-300 line-clamp-1" data-id="element-264">
                "Hi Sarah, The property is available this..."
              </div>
              {/* Subtle status indicator */}
              <div className="mt-2 flex items-center gap-2" data-id="element-265">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" data-id="element-266" />
                <span className="text-xs text-slate-500" data-id="element-267">AI-drafted • Ready</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Fixed-height orb section */}
        <div className="h-[160px] relative flex items-center justify-center" data-id="element-268">
          {/* Ripple effects behind orb */}
          <AnimatePresence data-id="element-269">
            {phase === 'listening' && <>
                {[0, 1, 2].map(i => <motion.div key={`${animationCycle}-ripple-${i}`} className="absolute w-32 h-32 rounded-full border border-indigo-400/20" initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 2 + i * 0.5,
              opacity: 0
            }} transition={{
              duration: 2,
              delay: i * 0.5,
              repeat: Infinity,
              ease: "easeOut"
            }} data-id="element-270" />)}
              </>}
          </AnimatePresence>

          {/* Main orb container - using transform: scale() to prevent layout shifts */}
          <motion.div className="relative w-24 h-24 rounded-full overflow-hidden" style={{
          transform: `scale(${phase === 'listening' ? 1.1 : 1})`,
          boxShadow: phase === 'listening' ? '0 0 40px rgba(99, 102, 241, 0.4), 0 0 80px rgba(99, 102, 241, 0.2)' : '0 0 20px rgba(99, 102, 241, 0.15)'
        }} transition={{
          type: "spring",
          stiffness: 300,
          damping: 25
        }} data-id="element-271">
            {/* Subtle idle breathing animation */}
            {phase === 'idle' && <motion.div className="absolute inset-0 bg-gradient-to-br from-indigo-400/20 via-purple-400/10 to-pink-400/20 rounded-full" animate={{
            scale: [1, 1.02, 1],
            opacity: [0.3, 0.5, 0.3]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }} data-id="element-272" />}
            {/* Gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 via-purple-400 to-pink-400 opacity-90" data-id="element-273" />
            
            {/* Animated mesh gradient overlay */}
            <motion.div className="absolute inset-0" animate={{
            background: ['radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.3) 0%, transparent 50%)', 'radial-gradient(circle at 80% 70%, rgba(168, 85, 247, 0.3) 0%, transparent 50%)', 'radial-gradient(circle at 50% 50%, rgba(236, 72, 153, 0.3) 0%, transparent 50%)']
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: "linear"
          }} data-id="element-274" />

            {/* Simple frequency bars inside orb */}
            <div className="absolute inset-0 flex items-center justify-center gap-1" data-id="element-275">
              {[0, 1, 2, 3, 4].map(i => <motion.div key={i} className="w-1 bg-white/80 rounded-full" animate={{
              height: phase === 'listening' ? [4, 20, 12, 24, 8] : 4,
              opacity: phase === 'listening' ? 0.9 : 0.4
            }} transition={{
              duration: 0.6,
              repeat: phase === 'listening' ? Infinity : 0,
              repeatType: "reverse",
              delay: i * 0.1
            }} data-id="element-276" />)}
            </div>

          </motion.div>

        </div>

        {/* Fixed-height transcription section - always reserves space */}
        <div className="h-[40px] flex items-center justify-center" data-id="element-277">
          <AnimatePresence data-id="element-278">
            {phase === 'listening' && transcriptionText && <motion.div key={`${animationCycle}-transcription`} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg border border-white/20 dark:border-slate-700/20" data-id="element-279">
                <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400" data-id="element-280">
                  {transcriptionText}
                </span>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Fixed-height status section */}
        <div className="h-[90px] flex items-center justify-center px-6 pb-6" data-id="element-281">
          <AnimatePresence mode="wait" data-id="element-282">
            {phase === 'idle' && <motion.div key={`${animationCycle}-idle`} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="text-center" data-id="element-283">
                {/* Simple dots indicating readiness */}
                <div className="flex justify-center gap-2" data-id="element-284">
                  <div className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600 animate-pulse" data-id="element-285" />
                  <div className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600 animate-pulse delay-100" data-id="element-286" />
                  <div className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600 animate-pulse delay-200" data-id="element-287" />
                </div>
              </motion.div>}

            {phase === 'listening' && <motion.div key={`${animationCycle}-listening`} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="text-center" data-id="element-288">
                <div className="text-xs text-indigo-500 dark:text-indigo-400 font-medium" data-id="element-289">
                  Listening...
                </div>
              </motion.div>}


            {phase === 'sending' && <motion.div key={`${animationCycle}-sending`} initial={{
            scale: 0,
            rotate: -180
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            type: "spring",
            stiffness: 500,
            damping: 25
          }} className="flex flex-col items-center gap-3" data-id="element-290">
                <motion.div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg" animate={{
              boxShadow: ['0 0 20px rgba(34, 197, 94, 0.4)', '0 0 30px rgba(34, 197, 94, 0.6)', '0 0 20px rgba(34, 197, 94, 0.4)']
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }} data-id="element-291">
                  <motion.svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" initial={{
                pathLength: 0
              }} animate={{
                pathLength: 1
              }} transition={{
                duration: 0.5,
                delay: 0.2
              }} data-id="element-292">
                    <motion.path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-293" />
                  </motion.svg>
                </motion.div>
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6
            }} className="text-xs text-green-600 dark:text-green-400 font-medium" data-id="element-294">
                  Message sent!
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
};

// Optimized step visibility hook with smooth, debounced transitions
const useStepVisibility = () => {
  const [visibleSteps, setVisibleSteps] = useState(new Set<string>());
  const [activeStep, setActiveStep] = useState<string>('detect');
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  useEffect(() => {
    // Separate observer for visibility tracking (lightweight)
    const visibilityObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSteps(prev => new Set([...prev, entry.target.id]));
        } else {
          setVisibleSteps(prev => {
            const newSet = new Set(prev);
            newSet.delete(entry.target.id);
            return newSet;
          });
        }
      });
    }, {
      threshold: 0
    } // Just track if visible at all
    );

    // Main observer for active step highlighting (optimized)
    const activeObserver = new IntersectionObserver(entries => {
      // Collect currently intersecting steps with their ratios
      const intersectingSteps = new Map<string, number>();
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          intersectingSteps.set(entry.target.id, entry.intersectionRatio);
        }
      });

      // Debounce active step updates to prevent rapid switching
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        if (intersectingSteps.size > 0) {
          // Find the step with highest intersection ratio
          const [bestStepId] = Array.from(intersectingSteps.entries()).sort(([, a], [, b]) => b - a)[0];
          setActiveStep(bestStepId);
        }
      }, 50); // 50ms debounce for smooth transitions
    }, {
      threshold: 0.5,
      // Single threshold at 50% visibility
      rootMargin: "-25% 0px -25% 0px" // Focus on center 50% of viewport
    });
    const stepElements = document.querySelectorAll('[data-step]');
    stepElements.forEach(el => {
      visibilityObserver.observe(el);
      activeObserver.observe(el);
    });
    return () => {
      visibilityObserver.disconnect();
      activeObserver.disconnect();
      clearTimeout(debounceTimerRef.current);
    };
  }, []);
  return {
    visibleSteps,
    activeStep
  };
};

// Progress line component with loading bar effect
const ProgressLine: React.FC<{
  progress: any;
}> = ({
  progress
}) => {
  const shouldReduceMotion = useReducedMotion();
  return <div className="absolute left-1/2 top-0 bottom-0 w-1 -translate-x-1/2 hidden md:block" data-id="element-295">
      {/* Background line */}
      <div className="absolute top-0 left-0 w-full h-full bg-slate-200/30 dark:bg-slate-700/30 [mask-image:linear-gradient(180deg,transparent,transparent_20%,rgba(0,0,0,0.05)_25%,rgba(0,0,0,0.1)_30%,rgba(0,0,0,0.2)_35%,rgba(0,0,0,0.4)_40%,rgba(0,0,0,0.6)_45%,rgba(0,0,0,0.8)_50%,black_55%)]" data-id="element-296" />
      
      {/* Animated progress line with loading bar effect */}
      <motion.div className="absolute top-0 left-0 w-full [mask-image:linear-gradient(180deg,transparent,transparent_20%,rgba(0,0,0,0.05)_25%,rgba(0,0,0,0.1)_30%,rgba(0,0,0,0.2)_35%,rgba(0,0,0,0.4)_40%,rgba(0,0,0,0.6)_45%,rgba(0,0,0,0.8)_50%,black_55%)]" style={{
      height: progress
    }} data-id="element-297">
        {/* Main progress bar */}
        <div className="absolute inset-0 bg-gradient-to-b from-sage-600/60 via-sage-500/40 to-sage-600/60" data-id="element-298" />
        
        {/* Glowing tip that connects to dot */}
        <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-sage-600 to-transparent" style={{
        marginBottom: '-10px'
      }} data-id="element-299" />
      </motion.div>
      
      {/* Pulsing dot with background ripples */}
      <motion.div className="absolute left-1/2 -translate-x-1/2" style={{
      top: progress
    }} data-id="element-300">
        {/* Outer ripple - smaller radius, behind everything */}
        <motion.div className="absolute -inset-2 rounded-full bg-sage-400/20" animate={shouldReduceMotion ? {} : {
        scale: [1, 1.8, 1],
        opacity: [0, 0.3, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: "easeOut"
      }} style={{
        zIndex: -3
      }} data-id="element-301" />
        
        {/* Middle ripple - smaller radius */}
        <motion.div className="absolute -inset-1.5 rounded-full bg-indigo-400/30" animate={shouldReduceMotion ? {} : {
        scale: [1, 1.5, 1],
        opacity: [0, 0.4, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: "easeOut",
        delay: 1
      }} style={{
        zIndex: -2
      }} data-id="element-302" />
        
        {/* Inner ripple - smaller radius */}
        <motion.div className="absolute -inset-1 rounded-full bg-indigo-400/40" animate={shouldReduceMotion ? {} : {
        scale: [1, 1.3, 1],
        opacity: [0, 0.5, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: "easeOut",
        delay: 2
      }} style={{
        zIndex: -1
      }} data-id="element-303" />
        
        {/* Fixed core dot - on top */}
        <div className="relative w-3 h-3 rounded-full bg-sage-600 shadow-lg" style={{
        boxShadow: '0 0 10px rgba(94, 130, 98, 0.8), 0 0 20px rgba(94, 130, 98, 0.4)',
        zIndex: 1
      }} data-id="element-304" />
      </motion.div>
    </div>;
};

// Enhanced global styles for performance
// Main component
export function HowItWorksModern() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    visibleSteps,
    activeStep
  } = useStepVisibility();
  return <>
      <section ref={sectionRef} className="relative py-12 md:py-24 overflow-hidden bg-gradient-to-b from-white to-linen-50/50" id="how-it-works" role="region" aria-label="How RealtyZen Works" data-id="element-305">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" data-id="element-306">
        <div className="absolute h-full w-full bg-[linear-gradient(30deg,#000_12%,transparent_12.5%,transparent_87%,#000_87.5%,#000)] bg-[length:10px_18px]" data-id="element-307" />
      </div>
      
      <div className="container relative" data-id="element-308">
        {/* Title and subtitle - matching other sections */}
        <div className="text-center mb-12 md:mb-20" data-id="element-309">
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-semibold mb-4 text-slate-900 dark:text-white" data-id="element-310">
            How RealtyZen Works
          </h2>
          <p className="text-lg md:text-xl font-medium text-gray-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed" data-id="element-311">
            From inbox to response in under 2 minutes
          </p>
        </div>
        
        <div className="relative max-w-7xl mx-auto" data-id="element-312">
          {/* Steps with mock UIs - clean 2-column layout */}
          <div className="space-y-16 md:space-y-32" data-id="element-313">
            {steps.map((step, index) => <div key={step.id} id={step.id} data-step data-id="element-314">
                <div className="flex flex-col md:grid md:grid-cols-2 gap-12 items-stretch" data-id="element-315">
                  {/* Text column - right-aligned within column for better balance */}
                  <div className="w-full flex justify-center md:justify-end mb-6 md:mb-0 min-h-0 md:min-h-[410px]" data-id="element-316">
                    <div className="max-w-xl" data-id="element-317">
                      <StepText step={step} isActive={activeStep === step.id} isVisible={visibleSteps.has(step.id)} data-id="element-318" />
                    </div>
                  </div>
                  
                  {/* Mock column - balanced width */}
                  <div className="w-full max-w-xl mx-auto md:mx-0 min-h-0 md:min-h-[410px] flex items-center" data-id="element-319">
                    <div className="w-full" data-id="element-320">
                      {step.id === 'detect' && <MockInboxUI isVisible={visibleSteps.has(step.id)} isActive={activeStep === step.id} data-id="element-321" />}
                      {step.id === 'filter' && <MockFilterUI isVisible={visibleSteps.has(step.id)} isActive={activeStep === step.id} data-id="element-322" />}
                      {step.id === 'draft' && <MockDraftUI isVisible={visibleSteps.has(step.id)} isActive={activeStep === step.id} data-id="element-323" />}
                      {step.id === 'approve' && <MockApproveUI isVisible={visibleSteps.has(step.id)} isActive={activeStep === step.id} data-id="element-324" />}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      </section>
    </>;
}